import React, { useState, useEffect, useCallback } from 'react';
import { X, CreditCard, Banknote, FileText, Trash2, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';

const MODE_ICONS = {
  CB:      <CreditCard size={14} />,
  CHEQUE:  <FileText size={14} />,
  ESPECES: <Banknote size={14} />,
};

const fmt = v => parseFloat(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const EMPTY_FORM = {
  mode_paiement: '',
  montant: '',
  date_reglement: new Date().toISOString().split('T')[0],
  reference: '',
};

export default function ReglementPanel({ devis, onClose, onSaved, apiFns = null }) {
  const [modes, setModes]       = useState([]);
  const [data, setData]         = useState({ reglements: [], total_regle: 0, total_ttc: 0, restant: 0 });
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm]         = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    try {
      const [modesRes, reglRes] = await Promise.all([
        api.getModesReglement(),
        apiFns?.get ? apiFns.get() : api.getReglements(devis.vente_id),
      ]);
      setModes(modesRes.data || []);
      const d = reglRes.data || { reglements: [], total_regle: 0, total_ttc: 0, restant: 0 };
      setData(d);
      // Pré-remplir le montant avec le restant dû
      setForm(f => ({ ...f, montant: d.restant > 0 ? String(d.restant) : '' }));
      return d;
    } catch {
      toast.error('Erreur lors du chargement des règlements');
      return null;
    } finally {
      setLoading(false);
    }
  }, [apiFns, devis.vente_id]);

  useEffect(() => { load(); }, [load]);

  const selectedMode = modes.find(m => m.code === form.mode_paiement);

  const handleSave = async () => {
    if (!form.mode_paiement) return toast.error('Choisissez un mode de paiement');
    const montant = parseFloat(form.montant);
    if (!montant || montant <= 0) return toast.error('Montant invalide');
    if (selectedMode?.exige_reference && !form.reference.trim())
      return toast.error(`Une référence est obligatoire pour le mode ${form.mode_paiement}`);

    setSaving(true);
    try {
      if (apiFns?.create) {
        await apiFns.create({
          mode_paiement:  form.mode_paiement,
          montant,
          date_reglement: form.date_reglement,
          reference:      form.reference.trim() || undefined,
        });
      } else {
        await api.createReglement(devis.vente_id, {
          commande_id:    devis.vente_id,
          client_id:      devis.entreprise_id,
          agence_id:      devis.agence_id,
          mode_paiement:  form.mode_paiement,
          montant,
          date_reglement: form.date_reglement,
          reference:      form.reference.trim() || undefined,
        });
      }
      toast.success('Règlement enregistré');
      setForm(EMPTY_FORM);
      const updatedData = await load();
      onSaved?.(updatedData);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (reglementId) => {
    if (!window.confirm('Supprimer ce règlement ?')) return;
    setDeletingId(reglementId);
    try {
      if (apiFns?.delete) {
        await apiFns.delete(reglementId);
      } else {
        await api.deleteReglement(devis.vente_id, reglementId);
      }
      toast.success('Règlement supprimé');
      const updatedData = await load();
      onSaved?.(updatedData);
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingId(null);
    }
  };

  const { reglements, total_regle, total_ttc, restant } = data;
  const isFullyPaid = restant <= 0.005;

  return (
    <div
      data-testid="reglement-panel"
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: 440,
        background: 'var(--card, #ffffff)',
        borderLeft: '1px solid var(--border)',
        zIndex: 1100,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
        pointerEvents: 'auto',
      }}
      onPointerDown={e => e.stopPropagation()}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px', borderBottom: '1px solid var(--border)' }}>
        <div>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>Règlements</p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{devis.numero}</p>
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
          <X size={20} />
        </button>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
          Chargement…
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* Bilan */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 20,
            background: 'var(--bg, #f9fafb)', borderRadius: 10, padding: '12px 14px',
            border: '1px solid var(--border)',
          }}>
            {[
              { label: 'Total TTC', value: fmt(total_ttc), muted: false },
              { label: 'Réglé', value: fmt(total_regle), muted: false },
              { label: 'Restant dû', value: fmt(restant), muted: false, highlight: restant > 0.005 },
            ].map(({ label, value, highlight }) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                <div style={{
                  fontSize: 14, fontWeight: 700,
                  color: highlight ? '#ef4444' : (label === 'Réglé' && total_regle > 0) ? '#16a34a' : 'var(--text)',
                }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          {/* Liste des règlements existants */}
          {reglements.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Règlements enregistrés
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {reglements.map(r => (
                  <div
                    key={r.reglement_id}
                    data-testid={`reglement-item-${r.reglement_id}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 8,
                      background: 'var(--bg, #f9fafb)', border: '1px solid var(--border)',
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)' }}>{MODE_ICONS[r.mode_paiement]}</span>
                    <div style={{ flex: 1, fontSize: 13 }}>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                        {r.mode_libelle || r.mode_paiement}
                        {r.reference && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · Réf. {r.reference}</span>}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 1 }}>
                        {fmtDate(r.date_reglement)}
                        {r.pl_sync_status === 'synced' && (
                          <span style={{ marginLeft: 6, color: '#16a34a', fontSize: 11 }}>● PL</span>
                        )}
                        {r.pl_sync_status === 'a_synchroniser' && (
                          <span style={{ marginLeft: 6, color: '#f59e0b', fontSize: 11 }}>● PL en attente</span>
                        )}
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginRight: 6 }}>
                      {fmt(r.montant)}
                    </div>
                    <button
                      data-testid={`delete-reglement-${r.reglement_id}`}
                      onClick={() => handleDelete(r.reglement_id)}
                      disabled={deletingId === r.reglement_id}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', opacity: deletingId === r.reglement_id ? 0.5 : 1 }}
                      title="Supprimer ce règlement"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message si tout est réglé */}
          {isFullyPaid && reglements.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
              background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)',
              borderRadius: 8, fontSize: 13, color: '#15803d', marginBottom: 16,
            }}>
              <AlertCircle size={16} />
              Facture entièrement réglée
            </div>
          )}

          {/* Formulaire ajout règlement (si restant > 0) */}
          {!isFullyPaid && (
            <>
              <p style={{ margin: '0 0 12px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <Plus size={12} style={{ marginRight: 4 }} />
                {reglements.length > 0 ? 'Ajouter un règlement partiel' : 'Saisir le règlement'}
              </p>

              {/* Mode de paiement */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                  Mode de paiement
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {modes.map(m => (
                    <button
                      key={m.code}
                      data-testid={`mode-${m.code}`}
                      onClick={() => setForm(f => ({ ...f, mode_paiement: m.code }))}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                        borderRadius: 8, border: '1.5px solid',
                        borderColor: form.mode_paiement === m.code ? 'var(--primary-light)' : 'var(--border)',
                        background: form.mode_paiement === m.code ? 'var(--primary-50)' : '#ffffff',
                        color: form.mode_paiement === m.code ? 'var(--primary)' : 'var(--text)',
                        cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        transition: 'all 0.15s',
                      }}
                    >
                      {MODE_ICONS[m.code]}
                      {m.libelle}
                    </button>
                  ))}
                </div>
              </div>

              {/* Montant */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  Montant
                  {restant > 0 && (
                    <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8, fontSize: 12 }}>
                      (restant : {fmt(restant)})
                    </span>
                  )}
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    data-testid="reglement-montant"
                    type="text"
                    inputMode="decimal"
                    value={form.montant}
                    onChange={e => {
                      const v = e.target.value.replace(',', '.');
                      if (v === '' || /^\d*\.?\d*$/.test(v)) setForm(f => ({ ...f, montant: v }));
                    }}
                    placeholder="0.00"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '10px 40px 10px 12px', borderRadius: 8,
                      border: '1.5px solid var(--border)', background: '#ffffff',
                      color: 'var(--text)', fontSize: 15, fontWeight: 600, outline: 'none',
                    }}
                  />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14 }}>€</span>
                </div>
              </div>

              {/* Date */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                  Date du règlement
                </label>
                <input
                  data-testid="reglement-date"
                  type="date"
                  value={form.date_reglement}
                  onChange={e => setForm(f => ({ ...f, date_reglement: e.target.value }))}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', borderRadius: 8,
                    border: '1.5px solid var(--border)', background: '#ffffff',
                    color: 'var(--text)', fontSize: 14,
                  }}
                />
              </div>

              {/* Référence */}
              {(selectedMode?.exige_reference || form.mode_paiement === 'CHEQUE') && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                    N° {form.mode_paiement === 'CHEQUE' ? 'de chèque' : 'de référence'} *
                  </label>
                  <input
                    data-testid="reglement-reference"
                    type="text"
                    value={form.reference}
                    onChange={e => setForm(f => ({ ...f, reference: e.target.value }))}
                    placeholder="Ex: 1234567"
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      padding: '10px 12px', borderRadius: 8,
                      border: '1.5px solid var(--border)', background: '#ffffff',
                      color: 'var(--text)', fontSize: 14,
                    }}
                  />
                </div>
              )}

              {/* Compte comptable */}
              {selectedMode && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, padding: '7px 12px', background: 'var(--bg)', borderRadius: 6, border: '1px solid var(--border)' }}>
                  Compte d'attente : <strong>{selectedMode.compte_comptable}</strong> — {selectedMode.libelle}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
        <button
          onClick={onClose}
          style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', cursor: 'pointer', fontWeight: 500 }}
        >
          Fermer
        </button>
        {!isFullyPaid && (
          <button
            data-testid="reglement-save-btn"
            onClick={handleSave}
            disabled={saving || !form.mode_paiement || !form.montant}
            style={{
              flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
              background: (!form.mode_paiement || !form.montant) ? 'var(--border)' : 'var(--primary)',
              color: '#fff',
              cursor: saving ? 'wait' : (!form.mode_paiement || !form.montant) ? 'not-allowed' : 'pointer',
              fontWeight: 600, fontSize: 14,
            }}
          >
            {saving ? 'Enregistrement…' : 'Enregistrer le règlement'}
          </button>
        )}
      </div>
    </div>
  );
}
